import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Typography,
  Popper,
  Box,
  Paper,
  TextField,
  Theme, ListItemButton } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { OrderMember } from 'graphql/wire-transfers/queries';
import { TFunction } from 'i18next';
import React, { useState, useRef, useEffect, FC } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    input: {
      width: '100%',
      '&:focus': { backgroundColor: theme.palette.background.default },
      '& .MuiFilledInput-root': {
        textTransform: 'none',
        fontSize: '0.875rem',
        color: 'black',
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        backgroundColor: theme.palette.background.default,
      },
      '& .Mui-disabled': { cursor: 'not-allowed' },
    },
  })
);

interface SearchAuthorizationProps {
  t: TFunction;
  members: OrderMember[];
  selectedMembers: string[];
  handledSelectAuth: (newAuth: string) => void;
}

const SearchAuthorization: FC<SearchAuthorizationProps> = ({
  members,
  handledSelectAuth,
  selectedMembers,
  t,
}) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorWidth, setAnchorWidth] = useState<number>();
  const popperRef = useRef<HTMLDivElement | null>(null);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setOpen(true);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const element = anchorRef.current ?? event.currentTarget;
    setAnchorEl(element);
    setAnchorWidth(element.offsetWidth);
    setOpen(true);
  };

  const handleSelect = (member) => {
    handledSelectAuth(member.login_email);
    setInputValue('');
    setOpen(false);
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      !anchorRef.current.contains(event.target as Node) &&
      popperRef.current &&
      !popperRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  const isSelected = (email) => selectedMembers?.some((member) => member === email);

  const filteredMembers = members.filter((member) =>
    member.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickAway);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
    };
  }, []);

  return (
    <Box>
      <TextField
        variant="filled"
        className={classes.input}
        ref={anchorRef}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        label={t('orders:authorizationsLabel')}
        placeholder={t('orders:searchMembersPlaceholder')}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <FontAwesomeIcon icon="user" />
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={open}
        anchorEl={anchorEl}
        style={{
          zIndex: 1300,
          width: anchorWidth,
        }}
        placement="bottom-start"
        ref={popperRef}
      >
        <Paper style={{ maxHeight: 200, overflowY: 'auto' }}>
          <List>
            {filteredMembers.map((member) => {
              const disabled = isSelected(member.login_email);
              return disabled ? (
                <ListItem key={member.login_email} style={{ backgroundColor: '#E0E0E0' }}>
                  <ListItemText primary={<Typography>{member.name}</Typography>} />
                </ListItem>
              ) : (
                <ListItemButton key={member.login_email} onClick={() => handleSelect(member)}>
                  <ListItemText primary={member.name} />
                </ListItemButton>
              );
            })}
            {filteredMembers.length === 0 && (
              <ListItem>
                <ListItemText primary={t('payments:no-options')} />
              </ListItem>
            )}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

export default SearchAuthorization;
